import dayjs from "dayjs";
import { FC } from "react";
import { Calendar, Users, Store, ClipboardCheck } from "lucide-react";
import { MonthlyHighlightCard } from "src/features/reporting/components";
import pluralize from "pluralize";
import totalStoresPassedObservationThresholdPercentage from "../../utils/totalStoresPassedObservationThresholdPercentage/totalStoresPassedObservationThresholdPercentage";
import totalUsersCompliantPercentage from "../../utils/totalUsersCompliantPercentage/totalUsersCompliantPercentage";
import { useFormContext, useWatch } from "react-hook-form";

interface DefaultProps {
	observationType: IObservationType;

	numStores: number;

	numEmployees: number;

	numObservationsCompleted: number;

	numObservationsCompliant: number;

	numStoresPassedThreshold: number;

	numEmployeesGraded: number;
}

export type Props = Partial<DefaultProps>;

const MonthlySummary: FC<Props> = ({
	observationType,
	numStores,
	numEmployees,
	numObservationsCompleted,
	numObservationsCompliant,
	numStoresPassedThreshold,
	numEmployeesGraded
}) => {
	const { control } = useFormContext<ReportDetailForm>();

	const { showGradingReport } = useWatch({
		control,
		name: "filters"
	});

	const observationsCompletedSubtitle = () => {
		if (observationType === "STORE_SELF_ASSESSMENT") {
			return `${pluralize("Assessment", numObservationsCompleted)} completed`;
		}

		if (observationType === "STORE_SCORED_ASSESSMENT") {
			return `${pluralize("Store", numObservationsCompleted)} visited`;
		}

		return `${pluralize("Employee", numObservationsCompleted)} observed`;
	};

	const showStoresPassedThreshold =
		observationType === "STORE_SCORED_ASSESSMENT" &&
		numStoresPassedThreshold !== undefined &&
		numObservationsCompleted !== undefined;

	const showObservationsCompliant =
		observationType === "INDIVIDUAL" &&
		numObservationsCompliant !== undefined &&
		numObservationsCompleted !== undefined;

	const showGreatConversationGraded =
		showGradingReport &&
		numEmployeesGraded !== undefined &&
		numObservationsCompleted !== undefined;

	const employeeObservedPercentage =
		numObservationsCompleted && numEmployees
			? Math.round((numObservationsCompleted / numEmployees) * 100)
			: 0;

	const gradedPercentage =
		numEmployeesGraded && numObservationsCompleted
			? Math.round((numEmployeesGraded / numObservationsCompleted) * 100)
			: 0;

	const employeeObservedTotalWithPercentage = `${numObservationsCompleted}/${numEmployees} (${employeeObservedPercentage}%)`;

	const employeeGradedTotalWithPercentage = `${numEmployeesGraded}/${numObservationsCompleted} (${gradedPercentage}%)`;

	return (
		<>
			<h2 className="font-heading text-lg font-medium text-zinc-800 mb-4">
				Current month summary
			</h2>

			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
				<MonthlyHighlightCard
					heading={dayjs().format("MMM")}
					subtitle={dayjs().format("YYYY")}
					icon={<Calendar className="h-5 w-5" />}
				/>

				{numStores !== undefined && (
					<MonthlyHighlightCard
						heading={numStores.toString()}
						subtitle={pluralize("store", numStores)}
						icon={<Store className="h-5 w-5" />}
					/>
				)}

				{!showGradingReport && numEmployees !== undefined && (
					<MonthlyHighlightCard
						heading={numEmployees.toString()}
						subtitle={pluralize("Employee", numEmployees)}
						icon={<Users className="h-5 w-5" />}
					/>
				)}

				{numObservationsCompleted !== undefined && (
					<MonthlyHighlightCard
						heading={
							observationType !== "GREAT_CONVERSATION"
								? numObservationsCompleted.toString()
								: employeeObservedTotalWithPercentage
						}
						subtitle={observationsCompletedSubtitle()}
						icon={<ClipboardCheck className="h-5 w-5" />}
						borderColor="black"
						backgroundColor={
							observationType === "GREAT_CONVERSATION" &&
							!showGreatConversationGraded
								? "white"
								: "var(--color-muted)"
						}
						borderWidth={
							observationType === "GREAT_CONVERSATION" &&
							!showGreatConversationGraded
								? 2
								: 0
						}
					/>
				)}

				{showGreatConversationGraded && (
					<MonthlyHighlightCard
						heading={employeeGradedTotalWithPercentage}
						subtitle={`${pluralize(
							"Employee",
							Number(gradedPercentage)
						)} graded`}
						borderColor="black"
						borderWidth={2}
						backgroundColor="white"
						icon={<ClipboardCheck className="h-5 w-5" />}
					/>
				)}

				{showObservationsCompliant && (
					<MonthlyHighlightCard
						heading={totalUsersCompliantPercentage({
							totalUsersCompliant: numObservationsCompliant,
							totalUsersObserved: numObservationsCompleted
						})}
						subtitle="Overall compliance"
						icon={<ClipboardCheck className="h-5 w-5" />}
						borderColor="black"
						borderWidth={2}
						backgroundColor="white"
					/>
				)}

				{showStoresPassedThreshold && (
					<MonthlyHighlightCard
						heading={totalStoresPassedObservationThresholdPercentage({
							storesPassedThreshold: numStoresPassedThreshold,
							totalStoresObserved: numObservationsCompleted
						})}
						subtitle="Average visit score"
						icon={<ClipboardCheck className="h-5 w-5" />}
						borderColor="black"
						borderWidth={2}
						backgroundColor="white"
					/>
				)}
			</div>
		</>
	);
};

export default MonthlySummary;
